import React, {
	Dispatch,
	SetStateAction,
	useState,
	useEffect,
	useRef,
} from "react"
import classNames from "classnames"

import { ConsentCheckbox } from "../ConsentCheckbox/ConsentCheckbox"
import { EmailInput } from "../EmailInput"
import { SimpleModal } from "../SimpleModal"

import Logo from "../../../assets/_V2/svg/Logo"

import { newSubscriberFormSendEvent } from "../../../gtm-events"

import config from "../../../config"

import styles from "./styles.module.scss"

interface Props {
	isOpen: boolean
	onClose: Dispatch<SetStateAction<boolean>>
	modalData: {
		customTitle?: JSX.Element
		title?: string | JSX.Element
		details?: string
		action: string
		successTitle: string
		successDetails: string
		successAction: string
		logo?: JSX.Element
	}
	inputTitle: string
	className?: string
	showBackdropShape: boolean
}

export enum ACTION_STATUSES {
	SENDING = "SENDING",
	NOT_SEND = "NOT_SEND",
	SUCCESS = "SUCCESS",
	FAIL = "FAIL",
}

const formDataInitial = {
	email: {
		value: "",
		errorMessage: "",
		isFilled: false,
	},
}

export const RequestSendPulseModal: React.FC<Props> = props => {
	const [actionStatus, setActionStatus] = useState<ACTION_STATUSES>(
		ACTION_STATUSES.NOT_SEND
	)
	const [isFormFilled, setIsFormFilled] = useState<boolean>(false)
	const [isSuccessView, setIsSuccessView] = useState<boolean>(false)
	const [formData, setFormData] = useState(formDataInitial)
	const [authKey, setAuthKey] = useState("")

	useEffect(() => {
		fetch("https://api.sendpulse.com/oauth/access_token", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				grant_type: "client_credentials",
				client_id: config.SEND_PULSE_CLIENT_ID,
				client_secret: config.SEND_PULSE_CLIENT_SECRET,
			}),
		})
			.then(response => response.json())
			.then(response => {
				console.log(response)

				setAuthKey(response.token_type + " " + response.access_token)
			})
			.catch(error => console.log(error))
	}, [])

	useEffect(() => {
		if (Object.values(formData).every(field => field.isFilled)) {
			setIsFormFilled(true)
		} else {
			setIsFormFilled(false)
		}
	}, [formData])

	useEffect(() => {
		if (!props.isOpen) {
			setFormData(formDataInitial)

			setActionStatus(ACTION_STATUSES.NOT_SEND)
			setIsSuccessView(false)
		}
	}, [props.isOpen])

	useEffect(() => {
		if (isSuccessView) {
			successFormBtn.current.focus()
		}
	}, [isSuccessView])

	const defaultErrors: { email: string; name: string; phone: string } = {
		email: "Введите корректный email",
		name: "Введите корректное имя",
		phone: "Введите корректный номер телефона",
	}

	const handleInputChange = (
		value: string,
		isFilled: boolean,
		name: string,
		errorMessage?: string
	) => {
		if (!errorMessage) {
			errorMessage = defaultErrors[name]
		}
		setFormData({
			...formData,
			[name]: {
				errorMessage: !isFilled ? errorMessage : "",
				value,
				isFilled,
			},
		})
	}

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		setActionStatus(ACTION_STATUSES.SENDING)

		const id = config.SEND_PULSE_BOOK_ID

		fetch(`https://api.sendpulse.com/addressbooks/${id}/emails`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: authKey,
			},
			body: JSON.stringify({
				emails: [formData.email.value],
			}),
		})
			.then(response => response.json())
			.then(response => {
				if (response.result) {
					setIsSuccessView(true)

					setActionStatus(ACTION_STATUSES.SUCCESS)
					newSubscriberFormSendEvent()
				} else {
					setActionStatus(ACTION_STATUSES.NOT_SEND)
				}
				setFormData(formDataInitial)
			})
			.catch(error => {
				console.error("Request error", error)

				setFormData(formDataInitial)

				setActionStatus(ACTION_STATUSES.FAIL)
			})
	}

	const handleSubmitSuccess = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		props.onClose(!props.isOpen)
	}

	const successFormBtn = useRef()

	return (
		<SimpleModal
			isOpen={props.isOpen}
			onClose={props.onClose}
			isShowBackdropShape={props.showBackdropShape}
		>
			<section className={classNames(styles.content, props.className)}>
				{props.modalData.logo ? (
					props.modalData.logo
				) : (
					<Logo className={styles.logo} />
				)}
				{!isSuccessView ? (
					<div className={styles.wrapper}>
						{props.modalData.customTitle ? (
							props.modalData.customTitle
						) : (
							<h2 className={styles.title}>{props.modalData.title}</h2>
						)}

						{props.modalData.details && (
							<p className={styles.details}>{props.modalData.details}</p>
						)}

						<form className={styles.form} onSubmit={handleSubmit}>
							<EmailInput
								className={styles.emailInput}
								inputId={"emailInput"}
								inputTitle={props.inputTitle}
								value={formData.email.value}
								actionStatus={actionStatus}
								errorMessage={formData.email.errorMessage}
								onInputChange={(value, isInputFilled) => {
									handleInputChange(value, isInputFilled, "email")
								}}
								extraProps={{
									required: true,
									autoFocus: true,
								}}
							/>

							<ConsentCheckbox
								className={styles.legalCheckbox}
								isAcceptedByDefault={true}
							/>

							<button
								className={styles.button}
								type="submit"
								disabled={
									!isFormFilled ||
									actionStatus === ACTION_STATUSES.SUCCESS ||
									actionStatus === ACTION_STATUSES.SENDING
								}
							>
								{props.modalData.action}
							</button>
						</form>
					</div>
				) : (
					<div className={classNames(styles.wrapper, styles.wrapperSuccess)}>
						<h2 className={styles.title}>{props.modalData.successTitle}</h2>
						<p className={styles.details}>{props.modalData.successDetails}</p>

						<form className={styles.successForm} onSubmit={handleSubmitSuccess}>
							<button
								ref={successFormBtn}
								className={styles.button}
								type="submit"
							>
								{props.modalData.successAction}
							</button>
						</form>
					</div>
				)}
			</section>
		</SimpleModal>
	)
}
